import { getSentry } from './monitoring.js';
/**
 * Manually capture an exception and report it to Sentry
 * Use this for try-catch blocks or manual error reporting
 *
 * @param error The error to capture
 * @param context Optional context to include with the error
 * @returns The event ID from Sentry, or undefined if Sentry is not available
 *
 * @example
 * ```typescript
 * try {
 *   riskyOperation();
 * } catch (error) {
 *   captureError(error, {
 *     tags: { feature: 'checkout' },
 *     extra: { userId: user.id }
 *   });
 * }
 * ```
 */
export function captureError(error, context) {
    const sentry = getSentry();
    if (!sentry) {
        console.error('[Monitoring] Error captured (Sentry disabled):', error, context);
        return undefined;
    }
    return sentry.captureException(error, context);
}
/**
 * Manually capture a message and report it to Sentry
 * Use this for logging important events or warnings
 *
 * @param message The message to capture
 * @param context Optional context to include with the message
 * @returns The event ID from Sentry, or undefined if Sentry is not available
 *
 * @example
 * ```typescript
 * captureMessage('User completed onboarding', {
 *   level: 'info',
 *   tags: { feature: 'onboarding' }
 * });
 * ```
 */
export function captureMessage(message, context) {
    const sentry = getSentry();
    if (!sentry) {
        console.log('[Monitoring] Message captured (Sentry disabled):', message, context);
        return undefined;
    }
    return sentry.captureMessage(message, context);
}
/**
 * Identify the user on all future Sentry events by internal id only; pass null on logout
 *
 * @example
 * ```typescript
 * setUser({ id: '123' });
 * setUser(null);
 * ```
 */
export function setUser(user) {
    const sentry = getSentry();
    if (!sentry) {
        return;
    }
    sentry.setUser(user);
}
/**
 * Set a tag for all future Sentry events
 *
 * @param key Tag key
 * @param value Tag value
 *
 * @example
 * ```typescript
 * setTag('environment', 'production');
 * ```
 */
export function setTag(key, value) {
    const sentry = getSentry();
    if (!sentry) {
        return;
    }
    sentry.setTag(key, value);
}
/**
 * Set multiple tags for all future Sentry events
 *
 * @param tags Object containing tag key-value pairs
 *
 * @example
 * ```typescript
 * setTags({
 *   environment: 'production',
 *   version: '1.2.3'
 * });
 * ```
 */
export function setTags(tags) {
    const sentry = getSentry();
    if (!sentry) {
        return;
    }
    sentry.setTags(tags);
}
/**
 * Set context data for all future Sentry events
 *
 * @param name Context name
 * @param context Context data
 *
 * @example
 * ```typescript
 * setContext('checkout', {
 *   cart_value: 99.99,
 *   items_count: 3
 * });
 * ```
 */
export function setContext(name, context) {
    const sentry = getSentry();
    if (!sentry) {
        return;
    }
    sentry.setContext(name, context);
}
/**
 * Add a breadcrumb for debugging context
 * Breadcrumbs are shown in Sentry to help understand what led to an error
 *
 * @param breadcrumb Breadcrumb data
 *
 * @example
 * ```typescript
 * addBreadcrumb({
 *   message: 'User clicked checkout button',
 *   category: 'user-action',
 *   level: 'info',
 *   data: { cart_value: 99.99 }
 * });
 * ```
 */
export function addBreadcrumb(breadcrumb) {
    const sentry = getSentry();
    if (!sentry) {
        return;
    }
    sentry.addBreadcrumb(breadcrumb);
}
//# sourceMappingURL=error-reporting.js.map